import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useAddDomain } from '../../data/useInternalAdmin';

interface AddDomainDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
}

const AddDomainDialog: React.FC<AddDomainDialogProps> = ({
  open,
  onClose,
  accountId,
}) => {
  const addDomain = useAddDomain(accountId);
  const [domain, setDomain] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = () => {
    setDomain('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      await addDomain.mutateAsync(domain.trim().toLowerCase());
      handleClose();
    } catch (err: any) {
      const detail =
        err?.response?.data?.errors?.[0]?.detail ||
        err?.response?.data?.detail ||
        'Failed to add domain. It may already be registered.';
      setError(detail);
    }
  };

  const isValid = domain.trim().length > 0 && domain.trim().includes('.');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Email Domain</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <TextField
          label="Domain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          fullWidth
          margin="dense"
          placeholder="example.com"
          helperText="Emails from this domain will be routed to this account."
          autoFocus
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || addDomain.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDomainDialog;
